import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Pricing.css';

const Pricing = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const plansRes = await api.get('/payments/plans');
        setPlans(plansRes.data);
        if (user) {
          const subRes = await api.get('/payments/my-subscription').catch(() => ({ data: null }));
          setCurrentSub(subRes.data);
        }
      } catch (err) {
        setError(t('pricing.failedPlans'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      window.location.href = '/lms/login';
      return;
    }
    setSubscribing(planId);
    setError('');
    try {
      const res = await api.post('/payments/create-subscription', { plan_id: planId });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || t('pricing.failedCheckout'));
      setSubscribing(null);
    }
  };

  const getFeatures = (features) => {
    if (!features) return [];
    if (Array.isArray(features)) return features;
    if (typeof features === 'object') return Object.values(features);
    return [];
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="page pricing-page">
      <div className="container">
        <section className="pricing-hero">
          <p className="eyebrow">{t('pricing.eyebrow')}</p>
          <h1>{t('pricing.title')}</h1>
          <p className="pricing-subtitle">{t('pricing.subtitle')}</p>
        </section>

        {searchParams.get('subscribed') === '1' && (
          <div className="success-banner" role="status" aria-live="polite">
            {t('pricing.subscriptionActivated')}
          </div>
        )}

        {error && <div className="error-message" role="alert">{error}</div>}

        {currentSub && (
          <div className="current-plan-banner">
            {t('pricing.activePlan')}: <strong>{currentSub.name}</strong> — {t('pricing.expires')}{' '}
            {new Date(currentSub.expires_at).toLocaleDateString()}
          </div>
        )}

        {plans.length === 0 ? (
          <div className="no-plans">
            <p>{t('pricing.noPlans')}</p>
            <Link to="/courses" className="btn-primary">{t('nav.allCourses')}</Link>
          </div>
        ) : (
          <div className="plans-grid" role="list">
            {plans.map((plan) => {
              const features = getFeatures(plan.features);
              const isActive = currentSub?.plan_id === plan.id;
              return (
                <article
                  key={plan.id}
                  className={`plan-card ${plan.is_featured ? 'plan-featured' : ''} ${isActive ? 'plan-active' : ''}`}
                  role="listitem"
                  aria-label={`${plan.name} plan, $${plan.price} per month`}
                >
                  {plan.is_featured && <span className="featured-badge">{t('pricing.mostPopular')}</span>}
                  {isActive && <span className="active-badge">{t('pricing.currentPlanBadge')}</span>}
                  <div className="plan-header">
                    <h2 className="plan-name">{plan.name}</h2>
                    <div className="plan-price">
                      <span className="price-amount">${plan.price}</span>
                      <span className="price-period">{t('pricing.perMonth')}</span>
                    </div>
                    {plan.description && <p className="plan-desc">{plan.description}</p>}
                  </div>

                  {features.length > 0 && (
                    <ul className="plan-features" aria-label="Plan features">
                      {features.map((f, i) => (
                        <li key={i}>
                          <span className="feature-check" aria-hidden="true">✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    className={`plan-btn ${plan.is_featured ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id || isActive}
                  >
                    {isActive
                      ? t('pricing.active')
                      : subscribing === plan.id
                      ? t('common.loading')
                      : t('pricing.getStarted')}
                  </button>
                </article>
              );
            })}
          </div>
        )}

        <section className="pricing-faq">
          <h2>{t('pricing.faqTitle')}</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>{t('pricing.cancelQ')}</h3>
              <p>{t('pricing.cancelA')}</p>
            </div>
            <div className="faq-item">
              <h3>{t('pricing.includedQ')}</h3>
              <p>{t('pricing.includedA')}</p>
            </div>
            <div className="faq-item">
              <h3>{t('pricing.freeQ')}</h3>
              <p>{t('pricing.freeA')}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Pricing;
